import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import WorldBuilder from './pages/WorldBuilder';
import Dashboard from './pages/Dashboard';
import TVDashboard from './pages/TVDashboard';
import VRTest from './pages/VRTest';
import ProfilePage from './pages/ProfilePage';
import AvatarSandbox from './pages/AvatarSandbox';
import DeviceLink from './pages/DeviceLink';
import { linkPagePath } from './utils/deviceLinkCode';
import CreatorPayouts from './pages/CreatorPayouts';
import Marketplace from './pages/Marketplace';
import CreatorListings from './pages/CreatorListings';
import BuilderEmbedded from './pages/BuilderEmbedded';
import VRViewer from './pages/VRViewer';
import CouncilPage from './pages/CouncilPage';
import CouncilWelcome from './pages/CouncilWelcome';
import AvatarsPage from './pages/AvatarsPage';
import SupportPage from './pages/SupportPage';
import DocsPage from './pages/DocsPage';
import DeveloperPage from './pages/DeveloperPage';
import ExperiencesPage from './pages/ExperiencesPage';
import OAuthAuthorizePage from './pages/OAuthAuthorizePage';
import LoginPage from './pages/LoginPage';
import { VersionChecker } from './components/VersionChecker';
import MaintenanceGate from './components/MaintenanceGate';
import './styles/brandColors.css';

// Side-effect imports: configure global loaders and raycasting
import './utils/loaderConfig';
import { accelerateRaycast } from './utils/bvhRaycast';
import { ThemeProvider } from './theme/ThemeProvider';
import './App.css';

// Studio pulls in its own 3D viewer; load it only when someone opens it.
const Studio = React.lazy(() => import('./pages/Studio'));
const AdminPage = React.lazy(() => import('./pages/Admin'));
const Welcome = React.lazy(() => import('./pages/Welcome'));
const RedeemPage = React.lazy(() => import('./pages/RedeemPage'));
const SupportForum = React.lazy(() => import('./pages/SupportForum'));
const AvatarCreatePage = React.lazy(() => import('./pages/AvatarCreatePage'));
const PublicProfilePage = React.lazy(() => import('./pages/PublicProfilePage'));
const ConnectionsPage = React.lazy(() => import('./pages/ConnectionsPage'));
const MeetupPage = React.lazy(() => import('./pages/MeetupPage'));
const ClassPage = React.lazy(() => import('./pages/ClassPage'));
const MeetupsPage = React.lazy(() => import('./pages/MeetupsPage'));
const PrivacyPage = React.lazy(() => import('./pages/legal/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/legal/TermsPage'));

// Enable BVH-accelerated raycasting globally (O(log n) vs O(n) per mesh)
accelerateRaycast();

// A world page brought back from the browser's back/forward cache resumes with
// its multiplayer and 3D state frozen mid-air; load it fresh instead.
window.addEventListener('pageshow', (e) => {
  if (e.persisted && /^\/(vr|world)\//.test(window.location.pathname)) window.location.reload();
});

// gathrxr.com/l/K7M42Q (shown in the headset): the link page with the code filled in.
function ShortDeviceLink() {
  const { code } = useParams();
  return <Navigate to={linkPagePath(code)} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <VersionChecker />
      <MaintenanceGate>
      <Router>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/welcome" element={<React.Suspense fallback={null}><Welcome /></React.Suspense>} />
          <Route path="/redeem" element={<React.Suspense fallback={null}><RedeemPage /></React.Suspense>} />
          {/* The console home is the dashboard now; the original list view
              stays reachable (and is what "Classic view" links to). */}
          <Route path="/dashboard" element={<TVDashboard />} />
          <Route path="/dashboard/classic" element={<Dashboard />} />
          <Route path="/tv" element={<TVDashboard />} />
          <Route path="/council" element={<CouncilPage />} />
          <Route path="/council/welcome" element={<CouncilWelcome />} />
          <Route path="/support" element={<React.Suspense fallback={null}><SupportForum /></React.Suspense>} />
          <Route path="/support/t/:threadId" element={<React.Suspense fallback={null}><SupportForum /></React.Suspense>} />
          <Route path="/donate" element={<SupportPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/privacy" element={<React.Suspense fallback={null}><PrivacyPage /></React.Suspense>} />
          <Route path="/terms" element={<React.Suspense fallback={null}><TermsPage /></React.Suspense>} />
          <Route path="/developer" element={<DeveloperPage />} />
          <Route path="/experiences" element={<ExperiencesPage />} />
          <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
          <Route path="/world/:worldId" element={<WorldBuilder />} />
          <Route path="/games/blackthorn" element={<VRViewer localBlackthorn />} />
          <Route path="/games/last-bite" element={<VRViewer localLastBite />} />
          <Route path="/games/carnival" element={<VRViewer localCarnival />} />
          <Route path="/vr/:worldId" element={<VRViewer />} />
          {/* "Try it": read-only walk-through of a world listing (no room, no saving). */}
          <Route path="/marketplace/preview/:listingId" element={<VRViewer />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/u/:username" element={<React.Suspense fallback={null}><PublicProfilePage /></React.Suspense>} />
          <Route path="/connections" element={<React.Suspense fallback={null}><ConnectionsPage /></React.Suspense>} />
          <Route path="/m/:slug" element={<React.Suspense fallback={null}><MeetupPage /></React.Suspense>} />
          <Route path="/class" element={<React.Suspense fallback={null}><ClassPage /></React.Suspense>} />
          <Route path="/class/:slug" element={<React.Suspense fallback={null}><ClassPage /></React.Suspense>} />
          <Route path="/meetups" element={<React.Suspense fallback={null}><MeetupsPage /></React.Suspense>} />
          <Route path="/avatars" element={<AvatarsPage />} />
          <Route path="/avatars/new" element={<React.Suspense fallback={null}><AvatarCreatePage /></React.Suspense>} />
          <Route path="/vr-test" element={<VRTest />} />
          <Route path="/avatar-sandbox" element={<AvatarSandbox />} />
          <Route path="/link" element={<DeviceLink />} />
          <Route path="/l/:code" element={<ShortDeviceLink />} />
          <Route path="/creator/payouts" element={<CreatorPayouts />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/creator/listings" element={<CreatorListings />} />
          <Route path="/builder-embedded" element={<BuilderEmbedded />} />
          <Route path="/studio" element={<React.Suspense fallback={null}><Studio /></React.Suspense>} />
          <Route path="/admin" element={<React.Suspense fallback={null}><AdminPage /></React.Suspense>} />
        </Routes>
      </Router>
      </MaintenanceGate>
    </ThemeProvider>
  );
}

export default App;
